import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "../../../utils/csrf.util"
import { rethrowUnlessAbort } from "../../../utils/fetch-error.util"

// Four language panes of one e-mail text. Every pane is its own form and its own
// draft: switching a tab, previewing or saving one language must never touch the
// other three (automation spec §5.1.3).
export default class extends Controller {
  static targets = ["pane", "tab", "field", "save", "saveLabel", "activeLocaleField"]
  static values = { activeLocale: String, previewUrl: String, saveLabelTemplate: String, unsavedMessage: String }

  #abortController = null
  #generation = 0
  #trackedField = null
  #dirtyLocales = new Set()

  connect() {
    document.addEventListener("turbo:before-stream-render", this.#onStreamRender)
    document.addEventListener("turbo:before-visit", this.#onBeforeVisit)
    window.addEventListener("beforeunload", this.#onBeforeUnload)
    this.#showLocale(this.activeLocaleValue)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.#onStreamRender)
    document.removeEventListener("turbo:before-visit", this.#onBeforeVisit)
    window.removeEventListener("beforeunload", this.#onBeforeUnload)
    this.#abortController?.abort()
    this.#abortController = null
  }

  switchTab(event) {
    this.#showLocale(event.currentTarget.dataset.locale)
  }

  // The pane that OWNS the edited field, not "the current tab" — a stream can
  // replace a pane while another one has focus.
  dirty(event) {
    this.#setDirty(this.#localeOf(event.target), true)
  }

  track(event) {
    this.#trackedField = event.target
  }

  // Inserts at the caret of the last focused field of the ACTIVE pane, so a chip
  // can never write into a hidden language. With nothing focused there yet, the
  // token goes to the end of that pane's body.
  insert(event) {
    const field = this.#insertionField()
    if (!field) return

    const start = field.selectionStart ?? field.value.length
    const end = field.selectionEnd ?? field.value.length
    field.setRangeText(event.currentTarget.dataset.token, start, end, "end")
    field.focus()
    this.#trackedField = field
    this.#setDirty(this.activeLocaleValue, true)
  }

  // Posts the ACTIVE pane's fields — never the form itself, which would carry
  // _method=patch and save instead of preview.
  async preview() {
    const locale = this.activeLocaleValue
    const body = this.#previewParams(locale)
    if (!body) return

    this.#abortController?.abort()
    const abortController = new AbortController()
    this.#abortController = abortController
    const generation = ++this.#generation

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": getCsrfToken() },
        body,
        signal: abortController.signal
      })
      // A trial that started mid-session answers 403 and a bug answers 500;
      // both bodies are useless. Raise so the failure reaches monitoring
      // instead of rendering nothing.
      if (!response.ok) throw new Error(`preview failed: ${response.status}`)

      const html = await response.text()
      // A superseded request, a disconnected editor or a language the user has
      // since left must not render over the page.
      if (generation !== this.#generation || !this.element.isConnected) return
      if (locale !== this.activeLocaleValue) return

      Turbo.renderStreamMessage(html)
    } catch (error) {
      rethrowUnlessAbort(error)
    } finally {
      if (this.#abortController === abortController) this.#abortController = null
    }
  }

  hidePreview() {
    document.getElementById("template_preview")?.replaceChildren()
  }

  #previewParams(locale) {
    const subject = this.#fieldFor(locale, "subject")
    const body = this.#fieldFor(locale, "body")
    if (!subject || !body) return null

    const params = new FormData()
    params.append("template[locale]", locale)
    params.append("template[subject]", subject.value)
    params.append("template[body]", body.value)
    return params
  }

  #showLocale(locale) {
    this.activeLocaleValue = locale
    // A tracked field in the pane we just left would let a chip insert into a
    // hidden language.
    this.#trackedField = null
    this.#applyActiveLocale()
    // A preview belongs to the language it was rendered for.
    this.hidePreview()
  }

  // Idempotent, and deliberately separate from #showLocale: it is re-run after
  // every pane replace, where clearing the preview or the caret would be a
  // change the user did not ask for.
  #applyActiveLocale() {
    const locale = this.activeLocaleValue
    this.paneTargets.forEach((pane) => {
      pane.hidden = pane.dataset.localePane !== locale
    })
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.locale === locale
      tab.setAttribute("aria-selected", String(active))
      tab.classList.toggle("active", active)
    })
    // Travels with the save, so the answer can tell whether the pane it
    // describes is still the one on screen.
    this.activeLocaleFieldTargets.forEach((field) => {
      field.value = locale
    })
    if (this.hasSaveTarget) this.saveTarget.setAttribute("form", `template_form_${locale}`)
    if (this.hasSaveLabelTarget) {
      this.saveLabelTarget.textContent = this.saveLabelTemplateValue.replace("%LOCALE%", locale.toUpperCase())
    }
  }

  // Arrow-function fields, not methods: they are added and removed as document
  // listeners, so each needs one stable bound identity.
  //
  // Turbo renders a 422 stream exactly like a 200 one, so "a pane came back" is
  // not the same as "it was saved" — only a pane that says data-pane-saved=true
  // holds the stored text and clears its language's unsaved mark.
  #onStreamRender = (event) => {
    const stream = event.detail?.newStream
    const match = (stream?.getAttribute("target") || "").match(/^template_pane_(\w+)$/)
    if (!match) return

    const incoming = stream.querySelector("template")?.content?.querySelector("[data-locale-pane]")
    if (incoming?.dataset.paneSaved === "true") this.#setDirty(match[1], false)

    // The server decides what the incoming pane says about its own visibility;
    // this is the belt to those braces — once Turbo HAS swapped it in, the tab
    // the user is actually on wins. Wrapping detail.render is the only ordering
    // guarantee: Turbo awaits it, while a setTimeout can (and in the browser
    // did) fire before the new pane is in the DOM.
    const render = event.detail.render
    if (typeof render !== "function") return

    event.detail.render = async (streamElement) => {
      await render(streamElement)
      if (this.element.isConnected) this.#applyActiveLocale()
    }
  }

  // Leaving the page with an unsaved language is worth a question; switching a
  // tab is not — nothing is lost there.
  #onBeforeVisit = (event) => {
    if (this.#dirtyLocales.size === 0) return

    const locales = [...this.#dirtyLocales].map((locale) => locale.toUpperCase()).join(", ")
    if (!window.confirm(this.unsavedMessageValue.replace("%LOCALES%", locales))) event.preventDefault()
  }

  // A reload or a closed tab loses a draft as completely as a Turbo visit does.
  // The browser shows its own wording; cancelling the event is all we control.
  #onBeforeUnload = (event) => {
    if (this.#dirtyLocales.size === 0) return

    event.preventDefault()
  }

  #setDirty(locale, dirty) {
    if (!locale) return

    if (dirty) this.#dirtyLocales.add(locale)
    else this.#dirtyLocales.delete(locale)

    const mark = this.tabTargets.find((tab) => tab.dataset.locale === locale)?.querySelector("[data-dirty-mark]")
    if (mark) mark.hidden = !dirty
  }

  // isConnected matters: a saved pane is REPLACED, so the field tracked before
  // the save is detached DOM whose closest() still reports the active locale —
  // inserting into it would swallow the chip silently.
  #insertionField() {
    const tracked = this.#trackedField
    if (tracked?.isConnected && this.#localeOf(tracked) === this.activeLocaleValue) return tracked

    return this.#fieldFor(this.activeLocaleValue, "body")
  }

  #fieldFor(locale, name) {
    return this.fieldTargets.find((field) => field.id === `template_${name}_${locale}`)
  }

  #localeOf(element) {
    return element?.closest("[data-locale-pane]")?.dataset.localePane
  }
}
